// frontend/src/components/calendar/PostDetailDrawer.js
import React, { useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    Button,
    Chip,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Alert,
    alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import RedditIcon from '@mui/icons-material/Reddit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import { scheduledPostsApi } from '../../services/api';
import { STATUS_COLORS, STATUS_LABELS } from './CALENDAR_CONFIG';

const PLATFORM_ICONS = {
    youtube: YouTubeIcon,
    instagram: InstagramIcon,
    twitter: TwitterIcon,
    linkedin: LinkedInIcon,
    facebook: FacebookIcon,
    reddit: RedditIcon,
};

const PostDetailDrawer = ({ open, post, onClose, onCancelled }) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState(null);

    if (!post) return null;

    const handleCancelClick = () => setConfirmOpen(true);
    const handleCancelClose = () => {
        setConfirmOpen(false);
        setError(null);
    };

    const handleConfirmCancel = async () => {
        setCancelling(true);
        setError(null);
        try {
            await scheduledPostsApi.cancel(post.id);
            onCancelled(post.id);
            handleCancelClose();
        } catch (err) {
            setError(err.message || 'Failed to cancel post');
        } finally {
            setCancelling(false);
        }
    };

    const formattedDate = dayjs(post.scheduledFor).format('dddd, MMM D, YYYY [at] h:mm A');

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 420 }, p: 0 }
                }}
                data-testid="post-detail-drawer"
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Scheduled Post Details</Typography>
                    <IconButton onClick={onClose} data-testid="drawer-close-btn">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />

                <Box sx={{ p: 3, overflowY: 'auto' }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Platforms</Typography>
                        <Stack direction="row" spacing={1}>
                            {post.platforms.map(p => {
                                const Icon = PLATFORM_ICONS[p];
                                return (
                                    <Chip
                                        key={p}
                                        icon={<Icon sx={{ fontSize: '1.2rem !important' }} />}
                                        label={p.charAt(0).toUpperCase() + p.slice(1)}
                                        variant="outlined"
                                    />
                                );
                            })}
                        </Stack>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scheduled for</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarMonthIcon color="action" />
                            <Typography data-testid="scheduled-time-display">{formattedDate}</Typography>
                        </Stack>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Status</Typography>
                        <Chip
                            label={STATUS_LABELS[post.status] || post.status}
                            size="small"
                            data-testid="post-status-chip"
                            sx={{
                                bgcolor: alpha(STATUS_COLORS[post.status] || '#6366f1', 0.1),
                                color: STATUS_COLORS[post.status] || '#6366f1',
                                fontWeight: 'bold',
                                border: `1px solid ${STATUS_COLORS[post.status] || '#6366f1'}`
                            }}
                        />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>Content</Typography>
                    {post.platforms.map(p => (
                        <Box key={p} sx={{ mb: 4 }} data-testid="platform-content-section">
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                {React.createElement(PLATFORM_ICONS[p], { sx: { fontSize: 20, color: 'primary.main' } })}
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                </Typography>
                            </Stack>

                            <Box sx={{ pl: 3.5 }}>
                                {post.platformContent[p]?.title && (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Title:</strong> {post.platformContent[p].title}
                                    </Typography>
                                )}
                                <Typography variant="body1" whiteSpace="pre-wrap">
                                    {post.platformContent[p]?.text || post.platformContent[p]?.caption || post.platformContent[p]?.description}
                                </Typography>
                            </Box>
                        </Box>
                    ))}

                    {post.mediaIds?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Media Attached</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {post.mediaIds.length} file(s) scheduled for upload.
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ mt: 6, mb: 2 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={handleCancelClick}
                            data-testid="cancel-post-btn"
                        >
                            Cancel Post
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Dialog
                open={confirmOpen}
                onClose={handleCancelClose}
                data-testid="confirm-cancel-dialog"
            >
                <DialogTitle>Cancel Scheduled Post?</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <DialogContentText>
                        This will remove the scheduled post from our queue. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={handleCancelClose}
                        disabled={cancelling}
                        data-testid="confirm-keep-btn"
                    >
                        Keep Post
                    </Button>
                    <Button
                        onClick={handleConfirmCancel}
                        color="error"
                        variant="contained"
                        autoFocus
                        disabled={cancelling}
                        data-testid="confirm-yes-btn"
                        startIcon={cancelling && <CircularProgress size={20} color="inherit" />}
                    >
                        {cancelling ? 'Cancelling...' : 'Yes, Cancel It'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PostDetailDrawer;
